import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Download, 
  Globe, 
  FileCode,
  CheckCircle,
  Info,
  ExternalLink,
  Copy,
  Check,
  Code,
  FileArchive
} from 'lucide-react';
import { toast } from 'sonner';

export function WordPressPluginManager() {
  const [copied, setCopied] = useState(false);
  const frontendUrl = import.meta.env.VITE_FRONTEND_URL || 'https://master.d25qg79z7w8v73.amplifyapp.com';
  const apiUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || 'https://api.atootbandan.com';

  const handleDownloadForWordPress = () => {
    // Create plugin files as Blob and download
    const pluginFiles = generateWordPressPlugin();
    
    toast.success('WordPress plugin package downloaded! Ready to upload to WordPress.org');
  };

  const handleDownloadForUsers = () => {
    toast.info('Direct users to: /wordpress-plugin route to download their personalized plugin');
  };

  const copyCode = (code: string, label: string) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopied(false), 2000);
  };

  const generateWordPressPlugin = () => {
    // This generates a GENERIC plugin for WordPress.org submission
    // Users will input their affiliate ID in settings after installation
    
    const mainPluginCode = `<?php
/**
 * Plugin Name: Atootbandan Matrimonial Ads
 * Plugin URI: ${frontendUrl}
 * Description: Display matrimonial advertisements and earn commission through affiliate program. Get your affiliate ID from Atootbandan.com
 * Version: 1.0.0
 * Author: Atootbandan
 * Author URI: ${frontendUrl}
 * License: GPL v2 or later
 * License URI: https://www.gnu.org/licenses/gpl-2.0.html
 * Text Domain: atootbandan-ads
 * Domain Path: /languages
 */

if (!defined('ABSPATH')) {
    exit;
}

define('ATOOTBANDAN_VERSION', '1.0.0');
define('ATOOTBANDAN_PLUGIN_DIR', plugin_dir_path(__FILE__));
define('ATOOTBANDAN_PLUGIN_URL', plugin_dir_url(__FILE__));
define('ATOOTBANDAN_API_URL', '${apiUrl}/api');
define('ATOOTBANDAN_FRONTEND_URL', '${frontendUrl}');

class Atootbandan_Matrimonial_Ads {
    private static $instance = null;
    
    public static function get_instance() {
        if (null === self::$instance) {
            self::$instance = new self();
        }
        return self::$instance;
    }
    
    private function __construct() {
        add_action('plugins_loaded', array($this, 'init'));
        register_activation_hook(__FILE__, array($this, 'activate'));
        register_deactivation_hook(__FILE__, array($this, 'deactivate'));
    }
    
    public function init() {
        load_plugin_textdomain('atootbandan-ads', false, dirname(plugin_basename(__FILE__)) . '/languages');
        
        if (is_admin()) {
            add_action('admin_menu', array($this, 'add_admin_menu'));
            add_action('admin_init', array($this, 'register_settings'));
            add_action('admin_notices', array($this, 'admin_notices'));
        }
        
        add_action('wp_enqueue_scripts', array($this, 'enqueue_scripts'));
        add_shortcode('atootbandan_ad', array($this, 'render_ad_shortcode'));
        add_action('widgets_init', array($this, 'register_widgets'));
    }
    
    public function activate() {
        if (!get_option('atootbandan_ad_size')) {
            add_option('atootbandan_ad_size', 'medium');
        }
        flush_rewrite_rules();
    }
    
    public function deactivate() {
        flush_rewrite_rules();
    }
    
    public function admin_notices() {
        $affiliate_id = get_option('atootbandan_affiliate_id');
        if (empty($affiliate_id)) {
            ?>
            <div class="notice notice-warning">
                <p><strong>Atootbandan Ads:</strong> Please enter your Affiliate ID in the settings to start earning commissions. <a href="<?php echo admin_url('admin.php?page=atootbandan-ads'); ?>">Configure Now</a></p>
            </div>
            <?php
        }
    }
    
    public function add_admin_menu() {
        add_menu_page(
            'Atootbandan Ads',
            'Atootbandan Ads',
            'manage_options',
            'atootbandan-ads',
            array($this, 'render_admin_page'),
            'dashicons-heart',
            30
        );
    }
    
    public function register_settings() {
        register_setting('atootbandan_settings', 'atootbandan_affiliate_id');
        register_setting('atootbandan_settings', 'atootbandan_ad_size');
    }
    
    public function render_admin_page() {
        include ATOOTBANDAN_PLUGIN_DIR . 'admin/settings-page.php';
    }
    
    public function enqueue_scripts() {
        wp_enqueue_style('atootbandan-ads-style', ATOOTBANDAN_PLUGIN_URL . 'assets/css/style.css', array(), ATOOTBANDAN_VERSION);
        wp_enqueue_script('atootbandan-ads-script', ATOOTBANDAN_PLUGIN_URL . 'assets/js/script.js', array('jquery'), ATOOTBANDAN_VERSION, true);
        
        wp_localize_script('atootbandan-ads-script', 'atootbandanAds', array(
            'apiUrl' => ATOOTBANDAN_API_URL,
            'frontendUrl' => ATOOTBANDAN_FRONTEND_URL,
            'affiliateId' => get_option('atootbandan_affiliate_id', ''),
            'nonce' => wp_create_nonce('atootbandan_ads_nonce')
        ));
    }
    
    public function render_ad_shortcode($atts) {
        $atts = shortcode_atts(array(
            'size' => get_option('atootbandan_ad_size', 'medium')
        ), $atts);
        
        ob_start();
        include ATOOTBANDAN_PLUGIN_DIR . 'templates/ad-display.php';
        return ob_get_clean();
    }
    
    public function register_widgets() {
        require_once ATOOTBANDAN_PLUGIN_DIR . 'includes/class-widget.php';
        register_widget('Atootbandan_Ads_Widget');
    }
}

Atootbandan_Matrimonial_Ads::get_instance();
`;

    // Create a Blob and download
    const blob = new Blob([mainPluginCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'atootbandan-matrimonial-ads.php';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold mb-2">WordPress Plugin Manager</h2>
        <p className="text-gray-600">
          Manage WordPress plugin downloads for affiliate program and WordPress.org publishing
        </p>
      </div>

      {/* User Plugin Download */}
      <Card className="border-2 border-rose-200">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Download className="size-5 text-rose-500" />
                User Plugin Downloads
              </CardTitle>
              <CardDescription className="mt-2">
                Users can download personalized plugins with their affiliate ID embedded
              </CardDescription>
            </div>
            <Badge className="bg-green-100 text-green-700 border-green-300">
              <CheckCircle className="size-3 mr-1" />
              Active
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <Info className="size-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900 mb-2">User Download Flow:</p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                  <li>User goes to "Referral & Wallet" section</li>
                  <li>Enables affiliate account (gets referral code)</li>
                  <li>Visits <code>/wordpress-plugin</code> route</li>
                  <li>Downloads personalized plugin with affiliate ID embedded</li>
                  <li>Installs on WordPress and starts earning!</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg border">
            <label className="text-sm font-medium mb-2 block">User Access URL:</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 p-3 bg-white border rounded text-sm">
                {frontendUrl}/wordpress-plugin
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyCode(`${frontendUrl}/wordpress-plugin`, 'URL')}
              >
                {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full"
            onClick={() => window.open('/wordpress-plugin', '_blank')}
          >
            <ExternalLink className="size-4 mr-2" />
            Preview User Download Page
          </Button>
        </CardContent>
      </Card>

      {/* WordPress.org Publishing */}
      <Card className="border-2 border-purple-200">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Globe className="size-5 text-purple-500" />
                WordPress.org Publishing
              </CardTitle>
              <CardDescription className="mt-2">
                Download generic plugin package for submitting to WordPress.org repository
              </CardDescription>
            </div>
            <Badge className="bg-purple-100 text-purple-700 border-purple-300">
              <FileArchive className="size-3 mr-1" />
              Generic Version
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-start gap-3">
              <Info className="size-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900 mb-2">WordPress.org Submission:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-amber-800">
                  <li>This version doesn't have affiliate ID pre-embedded</li>
                  <li>Users enter their affiliate ID in plugin settings after installation</li>
                  <li>Submit to WordPress.org for global distribution</li>
                  <li>Reach 40+ million WordPress sites worldwide</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid gap-3">
            <Button
              onClick={handleDownloadForWordPress}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              <Download className="size-4 mr-2" />
              Download Plugin for WordPress.org
            </Button>

            <Button
              variant="outline"
              className="w-full"
              onClick={() => window.open('https://wordpress.org/plugins/developers/', '_blank')}
            >
              <ExternalLink className="size-4 mr-2" />
              WordPress.org Developer Portal
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileCode className="size-5 text-rose-500" />
            Publishing Instructions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Step 1 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-bold">
                  1
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold mb-2">Download Generic Plugin</h4>
                <p className="text-sm text-gray-600 mb-2">
                  Click "Download Plugin for WordPress.org" above to get the complete plugin package
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-bold">
                  2
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold mb-2">Create WordPress.org Account</h4>
                <p className="text-sm text-gray-600 mb-2">
                  Register at wordpress.org if you haven't already
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open('https://wordpress.org/support/register.php', '_blank')}
                >
                  <ExternalLink className="size-3 mr-1" />
                  Register
                </Button>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-bold">
                  3
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold mb-2">Submit Plugin</h4>
                <p className="text-sm text-gray-600 mb-2">
                  Go to WordPress Plugin Developer page and submit your plugin
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open('https://wordpress.org/plugins/developers/add/', '_blank')}
                >
                  <ExternalLink className="size-3 mr-1" />
                  Submit Plugin
                </Button>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-bold">
                  4
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold mb-2">Wait for Approval</h4>
                <p className="text-sm text-gray-600">
                  WordPress team reviews submissions within 2-5 business days. Once approved, you'll get SVN repository access to publish your plugin.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>WordPress Plugin Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg">
              <div className="text-sm opacity-90 mb-1">Total Downloads</div>
              <div className="text-2xl font-bold">Coming Soon</div>
            </div>
            
            <div className="p-4 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg">
              <div className="text-sm opacity-90 mb-1">Active Installations</div>
              <div className="text-2xl font-bold">Coming Soon</div>
            </div>
            
            <div className="p-4 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg">
              <div className="text-sm opacity-90 mb-1">Total Clicks</div>
              <div className="text-2xl font-bold">Coming Soon</div>
            </div>
          </div>
          
          <p className="text-sm text-gray-600 mt-4">
            Statistics will be available once you integrate WordPress.org tracking
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
